import React from 'react'
import styled from 'styled-components'
import * as zod from 'zod'
import { useNativeFormMutation } from '@mote/api'
import { getUserAgent } from '@mote/helpers/browser'
import { WEB_STORAGE_PREFIX_KEYS } from '@mote/helpers/constants'
import { useCustomForm } from '@mote/helpers/hooks/business'
import {
  Alert,
  Avatar,
  Block,
  Button,
  Form,
  FormGroup,
  FormInput,
  H4,
  P
} from '@mote/ui'
import { Content, DefaultLayout } from '../layouts'
import { GetMoteLink } from '../shared/GetMoteLink'

const FORM_SCHEMA = zod.object({
  uninstalledReason: zod
    .string({ invalid_type_error: 'Please enter at least one character' })
    .min(1, { message: 'Please enter a reason for uninstallation' })
})

const textProps = {
  fontSize: 18,
  lineHeight: '140%',
  fontWeight: 400,
  color: '#6C6C84'
}

export const UninstalledScreen = () => {
  const { control, handleSubmit, getValues } = useCustomForm({
    schema: FORM_SCHEMA,
    defaultValues: { uninstalledReason: '' }
  })
  const { mutate: sendToNativeForm, error } = useNativeFormMutation()
  const [showThankYou, setShowThankYou] = React.useState(false)
  const timeoutId = React.useRef(null)

  React.useEffect(() => {
    return () => {
      if (timeoutId.current) clearTimeout(timeoutId.current)
    }
  }, [])

  const submitForm = React.useCallback(() => {
    if (timeoutId.current !== null) return

    const formValues = getValues()
    setShowThankYou(true)
    sendToNativeForm({
      variables: {
        airtableBaseId: 'apparTjZpDQYXvqYd',
        airtableTableId: 'tblXMkPhM1YAQUwUb',
        formData: {
          userId: window.localStorage.getItem(
            `${WEB_STORAGE_PREFIX_KEYS}userId`
          ),
          lastUserId: window.localStorage.getItem(
            `${WEB_STORAGE_PREFIX_KEYS}lastUserId`
          ),
          anonymousUserId: window.localStorage.getItem(
            `${WEB_STORAGE_PREFIX_KEYS}anonymousUserId`
          ),
          primaryEmailAddress: window.localStorage.getItem(
            `${WEB_STORAGE_PREFIX_KEYS}primaryEmailAddress`
          ),
          reason: formValues.uninstalledReason,
          userAgent: getUserAgent(),
          dateCreated: new Date().toISOString()
        }
      }
    }).catch(console.error)

    timeoutId.current = setTimeout(() => {
      timeoutId.current = null
      setShowThankYou(false)
    }, 1000)
  }, [getValues])

  return (
    <DefaultLayout withMargins={false}>
      <Content>
        <Container align='center' justify='space-between' spreadX spreadY>
          <Block gap={28}>
            <H4>Sorry to see you leave</H4>
            <Avatar
              src='images/photos/alex2.jpg'
              size={150}
              alt='Alex, the sad Engineer'
            />
            <P {...textProps}>
              Hi, I'm Alex, one of the founders at Mote. We're a small team,
              working really hard to make a product that people love.
            </P>
            <P {...textProps}>
              Would you consider taking a moment to help us understand what went
              wrong?
            </P>
            <P {...textProps}>
              Or if you're looking to reinstall Mote, click below:
            </P>
            <GetMoteLink />
          </Block>

          <FormContainer gap={32} spreadX>
            <H4>Please help us improve</H4>
            <Form gap={24} onSubmit={handleSubmit(submitForm)}>
              <FormInput
                name='uninstalledReason'
                label="What's the main reason you're removing Mote?"
                placeholder='Please enter a reason...'
                type='textarea'
                control={control}
                inputProps={{ rows: 12, maxWidth: '100%' }}
                rootProps={{ gap: 24 }}
                required
              />
              <FormGroup gap={24}>
                <Button htmlType='submit' text='Submit' autoWidth />
                {error && (
                  <Alert
                    type='error'
                    message='Something went wrong while submitting your feedback. Please refresh the page and try again!'
                    style={{ margin: 0 }}
                  />
                )}
                {!error && showThankYou && (
                  <Alert
                    type='success'
                    message='Thank you for your feedback!'
                  />
                )}
              </FormGroup>
            </Form>
          </FormContainer>
        </Container>
      </Content>
    </DefaultLayout>
  )
}

const Container = styled(Block)`
  flex-direction: row;
  gap: 90px;
  margin: 64px 0;

  @media (max-width: 991.98px) {
    flex-direction: column;
    gap: 40px;
  }

  @media (max-width: 767.98px) {
    margin-top: 48px;
  }

  @media (max-width: 639.98px) {
    margin-top: 32px;
  }
`

const FormContainer = styled(Block)`
  background: #f6f6f9;
  padding: 32px;
  border-radius: 8px;
  max-width: 464px;

  @media (max-width: 991.98px) {
    max-width: 100%;
  }

  @media (max-width: 639.98px) {
    padding: 24px;
  }
`
